import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import AfterSalesLayout from "../aftersaleslayout/AfterSalesLayout";
import BackBlue from "../../../assets/aftersalesservice_icons/back_blue_icon.svg";
import CheckWarrantyStatusIcon from "../../../assets/aftersalesservice_icons/check_warranty_status.svg";
import DownloadWarranty from "../../../assets/aftersalesservice_icons/download_warranty.svg";
import GeneralIndoor from "../../../assets/aftersalesservice_icons/general_indoor.svg";
import GeneralOutdoor from "../../../assets/aftersalesservice_icons/general_outdoor.svg";
import { ShieldCheck, ShieldEllipsis, Box } from "lucide-react";
import FallbackImg from "../../../components/FallbackImg";
import ApiService from "../../../components/Api_services";
import { BACKEND_URL } from "../../../constants";
import { useLanguage } from "../LanguageContext";
import {
  getAfterSalesApiSerial,
  getAfterSalesSerialType,
} from "../serialTypeUtils";

const CheckWarrantyStatus = () => {
  const { trans } = useLanguage();
  const [searchParams] = useSearchParams();
  const sn = atob(searchParams.get("sn") || "");
  const getSerialNo = () => sn;
  const serialNoForApi = getAfterSalesApiSerial(searchParams);
  const serialType = getAfterSalesSerialType(searchParams);

  const [warrantyItemList, setWarrantyItemList] = useState([]);
  const [customerData, setCustomerData] = useState([]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleString("en-MY", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  useEffect(() => {
    const fetchWarrantyInfo = async () => {
      const serialNo = serialNoForApi;

      try {
        const response = await ApiService.getWarrantyInfo(serialNo, serialType);

        if (response && response.status === 200) {
          const items = response.data?.warranty_item_list || [];
          // ensure each item has serial_no
          const normalizedItems = (Array.isArray(items) ? items : []).map(
            (it) => {
              const { item_warranty_period, ...rest } = it || {};
              return {
                ...rest, // rest no longer contains item_warranty_period
                // map item_warranty_period -> warranty_day (ensure numeric)
                warranty_day:
                  rest?.warranty_day ??
                  (item_warranty_period != null
                    ? Number(item_warranty_period)
                    : 0),
              };
            },
          );
          setWarrantyItemList(normalizedItems);
          setCustomerData(response.data?.customer_data);
        }
      } catch (error) {
        console.error("Error fetching warranty info:", error);
      }
    };

    fetchWarrantyInfo();
  }, [serialNoForApi, serialType]);

  const registeredDate =
    customerData?.purchase_date ||
    warrantyItemList[0]?.warranty?.[0]?.start_date ||
    null;

  const indoor =
    warrantyItemList.length === 1
      ? warrantyItemList[0]
      : (warrantyItemList.find((item) => item.type === "INDOOR") ?? null);
  const outdoor =
    warrantyItemList.length === 1
      ? null
      : (warrantyItemList.find((item) => item.type === "OUTDOOR") ?? null);

  const calcWarrantyDisplay = (w) => {
    const now = new Date();
    const wStart = w.start_date ? new Date(w.start_date) : null;
    const wEnd = w.expiry_date ? new Date(w.expiry_date) : null;

    let percent = 0;
    let nice = "—";

    if (wStart && wEnd && wEnd > wStart) {
      const total = wEnd.getTime() - wStart.getTime();
      const remainingMs = Math.max(0, wEnd.getTime() - now.getTime());
      percent = Math.round((remainingMs / total) * 100);

      if (remainingMs <= 0) {
        nice = "Expired";
      } else {
        // PHP's diff() equivalent logic
        let tempDate = new Date(now);
        let years = 0,
          months = 0,
          days = 0;

        // 1. Calculate Years
        years = wEnd.getFullYear() - tempDate.getFullYear();
        tempDate.setFullYear(tempDate.getFullYear() + years);
        if (tempDate > wEnd) {
          years--;
          tempDate.setFullYear(tempDate.getFullYear() - 1);
        }

        // 2. Calculate Months
        while (true) {
          let nextMonth = new Date(tempDate);
          nextMonth.setMonth(nextMonth.getMonth() + 1);
          if (nextMonth > wEnd) break;
          tempDate = nextMonth;
          months++;
        }

        // 3. Calculate Days
        const diffDays = Math.floor((wEnd - tempDate) / (1000 * 60 * 60 * 24));
        days = diffDays;

        const parts = [];
        if (years > 0) parts.push(`${years}y`);
        if (months > 0) parts.push(`${months}m`);
        if (days > 0) parts.push(`${days}d`);

        // 4. Fallback to Hours (if less than 1 day total)
        if (parts.length === 0) {
          const hours = Math.floor(remainingMs / (1000 * 60 * 60));
          if (hours > 0) parts.push(`${hours}h`);
        }

        nice = parts.join(" ") || "0d";
      }
    }

    return {
      title: w.name ?? w.warranty_id ?? "Warranty",
      coverage_period: w.duration ? `${parseFloat(w.duration)} Years` : "",
      start_date: w.start_date ?? "",
      expiry_date: w.expiry_date ?? "",
      percent,
      nice,
    };
  };

  const indoorWithCalc = indoor
    ? {
        ...indoor,
        warranty: indoor.warranty.map((w) => ({
          ...w,
          ...calcWarrantyDisplay(w),
        })),
      }
    : null;

  const outdoorWithCalc = outdoor
    ? {
        ...outdoor,
        warranty: outdoor.warranty.map((w) => ({
          ...w,
          ...calcWarrantyDisplay(w),
        })),
      }
    : null;

  const ProgressRing = ({ percent = 0, expired = false }) => {
    const circumference = 251.2;
    const offset = circumference - (percent / 100) * circumference;
    const strokeColor = expired ? "#E7000B" : "#10B981";
    const label = expired ? "Expired" : `${percent}% left`;

    return (
      <div className="as-progress-wrap">
        <svg viewBox="0 0 100 100" style={{ width: 90, height: 90 }}>
          <circle
            cx="50"
            cy="50"
            r="40"
            stroke="#E5E7EB"
            strokeWidth="5"
            fill="none"
          />
          <circle
            cx="50"
            cy="50"
            r="40"
            stroke={strokeColor}
            strokeWidth="5"
            fill="none"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            transform="rotate(-90 50 50)"
          />
        </svg>
        <div
          className="as-progress-text"
          style={{
            color: strokeColor,
            fontStyle: expired ? "italic" : "normal",
          }}
        >
          {label}
        </div>
      </div>
    );
  };

  const getShortName = (title = "") => {
    const lower = title.toLowerCase();
    if (lower.includes("general")) return trans.general;
    if (lower.includes("compressor")) return trans.compress;
    return title;
  };

  const IndoorUnit = ({ unit }) => {
    if (!unit) return null;

    return (
      <div className="as-coverage-box as-blue">
        <div className="as-unit-header">
          <div className="as-unit-icon-box as-blue">
            <Box size={24} color={"#fff"} />
          </div>
          <div className="as-unit-title as-blue">
            {warrantyItemList.length === 1 ? trans.unit : trans.indoor_unit}
          </div>
        </div>

        {(unit.warranty ?? []).map((w, i) => {
          const expired = isExpired(w.expiry_date);
          const expiredStyle = expired ? { color: "#E7000B" } : {};
          return (
            <div
              key={i}
              className={`as-warranty-card as-blue${expired ? " as-expired" : ""}`}
            >
              <div className="as-warranty-item">
                <div className="as-warranty-left">
                  <div className="as-w-header">
                    <FallbackImg
                      src={GeneralIndoor}
                      alt="generalindoor"
                      className="as-w-icon"
                    />
                    <div className="as-w-title as-blue" style={expiredStyle}>
                      {w.title || trans.general_warr}
                    </div>
                  </div>
                  <div className="as-w-remaining" style={expiredStyle}>
                    {w.nice || ""}
                  </div>
                  <div className="as-w-note as-blue" style={expiredStyle}>
                    {trans.remain_warr}
                  </div>
                </div>
                <ProgressRing percent={w.percent ?? 0} expired={expired} />
              </div>
            </div>
          );
        })}

        {/* Meta */}
        <div className="as-meta-container as-blue">
          <div className="as-meta-row as-blue">
            <div className="as-meta-label as-blue">{trans.serial_number}</div>
            <div className="as-meta-value as-blue">{unit.serial_no || "—"}</div>
          </div>

          <div
            className="as-coverage-header as-blue"
            style={{ margin: "12px 0 8px 0", color: "#1447E6" }}
          >
            {trans.cover_period}
          </div>
          {(unit.warranty ?? []).map((w, i) => (
            <div key={i} className="as-meta-row as-blue">
              <div className="as-meta-sub as-blue">
                {getShortName(w.title)}
              </div>
              <div className="as-meta-value as-blue">
                {w.coverage_period || "—"}
              </div>
            </div>
          ))}

          <div style={{ height: 12 }} />

          <div
            className="as-coverage-header as-blue"
            style={{ margin: "0 0 8px 0", color: "#1447E6" }}
          >
            {trans.exp_on}
          </div>
          {(unit.warranty ?? []).map((w, i) => {
            const expired = isExpired(w.expiry_date);
            return (
              <div key={i} className="as-meta-row as-blue">
                <div className="as-meta-sub as-blue">
                  {getShortName(w.title)}
                </div>
                <div
                  className="as-meta-value as-gray"
                  style={expired ? { color: "#E7000B" } : {}}
                >
                  {formatDate(w.expiry_date)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const OutdoorUnit = ({ unit }) => {
    if (!unit) return null;

    return (
      <div className="as-coverage-box as-purple">
        <div className="as-unit-header">
          <div className="as-unit-icon-box as-purple">
            <Box size={24} color={"#fff"} />
          </div>
          <div className="as-unit-title as-purple">{trans.outdoor_unit}</div>
        </div>

        {(unit.warranty ?? []).map((w, i) => {
          const expired = isExpired(w.expiry_date);
          const expiredStyle = expired ? { color: "#E7000B" } : {};
          return (
            <div
              key={i}
              className={`as-warranty-card as-purple${expired ? " as-expired" : ""}`}
            >
              <div className="as-warranty-item">
                <div className="as-warranty-left">
                  <div className="as-w-header">
                    <FallbackImg
                      src={GeneralOutdoor}
                      alt="generaloutdoor"
                      className="as-w-icon"
                    />
                    <div className="as-w-title as-purple" style={expiredStyle}>
                      {w.title || trans.general_warr}
                    </div>
                  </div>
                  <div className="as-w-remaining" style={expiredStyle}>
                    {w.nice || ""}
                  </div>
                  <div className="as-w-note as-purple" style={expiredStyle}>
                    {trans.remain_warr}
                  </div>
                </div>
                <ProgressRing percent={w.percent ?? 0} expired={expired} />
              </div>
            </div>
          );
        })}

        {/* Meta */}
        <div className="as-meta-container as-purple">
          <div className="as-meta-row as-purple">
            <div className="as-meta-label as-purple">{trans.serial_number}</div>
            <div className="as-meta-value as-purple">
              {unit.serial_no || "—"}
            </div>
          </div>

          <div
            className="as-coverage-header as-purple"
            style={{ margin: "12px 0 8px 0", color: "#8200DB" }}
          >
            {trans.cover_period}
          </div>
          {(unit.warranty ?? []).map((w, i) => (
            <div key={i} className="as-meta-row as-purple">
              <div className="as-meta-sub as-purple">
                {getShortName(w.title)}
              </div>
              <div className="as-meta-value as-purple">
                {w.coverage_period || "—"}
              </div>
            </div>
          ))}

          <div style={{ height: 12 }} />

          <div
            className="as-coverage-header as-purple"
            style={{ margin: "0 0 8px 0", color: "#8200DB" }}
          >
            {trans.exp_on}
          </div>
          {(unit.warranty ?? []).map((w, i) => {
            const expired = isExpired(w.expiry_date);
            return (
              <div key={i} className="as-meta-row as-purple">
                <div className="as-meta-sub as-purple">
                  {getShortName(w.title)}
                </div>
                <div
                  className="as-meta-value as-gray"
                  style={expired ? { color: "#E7000B" } : {}}
                >
                  {formatDate(w.expiry_date)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const isExpired = (expiryDate) => {
    if (!expiryDate) return false;
    return new Date(expiryDate) < new Date();
  };

  const goBack = (e) => {
    e.preventDefault();
    window.history.back();
  };

  return (
    <AfterSalesLayout>
      <div className="as-canvas" id="check_warranty_status_modal">
        <div className="as-canvas-scale as-no-select">
          <div style={{ padding: "0 31.97px" }}>
            <div className="as-content" id="contentRoot">
              {/* Top Sheet */}
              <div className="as-sheet-top" id="sheetTop">
                <a
                  className="as-back-btn"
                  id="check_warranty_status_back"
                  onClick={goBack}
                >
                  <FallbackImg src={BackBlue} alt="backblue" />
                  <span>{trans.back}</span>
                </a>

                <div className="as-title-row">
                  <div className="as-icon-wrap">
                    <FallbackImg
                      src={CheckWarrantyStatusIcon}
                      alt="checkwarrantystatus"
                    />
                  </div>
                  <div>
                    <div className="as-action-title">{trans.warr_status}</div>
                    <div className="as-action-sub">{trans.warr_status_sub}</div>
                  </div>
                </div>

                <a
                  className="as-download-cta"
                  id="downloadCta"
                  href={`${BACKEND_URL}pdf_report/warranty_certificate/${btoa(getSerialNo())}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span className="as-btn-icon">
                    <FallbackImg
                      src={DownloadWarranty}
                      alt="downloadwarranty"
                    />
                  </span>
                  {trans.download_warr}
                </a>
              </div>

              {/* Registered Pill */}
              <div className="as-registered-pill" id="registeredPill">
                <div className="as-shield">
                  <ShieldCheck size={28} color={"#fff"} />
                </div>
                <div className="as-reg-info">
                  <div className="as-label">{trans.warr_reg_date}</div>
                  <div className="as-date" id="registeredDateText">
                    {formatDate(registeredDate)}
                  </div>
                </div>
              </div>

              {/* Main Card */}
              <div className="as-main-card" id="mainCard">
                <div id="coverageContainer">
                  {/* Coverage Header */}
                  <div className="as-coverage-header-wrapper">
                    <ShieldEllipsis size={20} color={"#0b5ed7"} />
                    <h3 className="as-coverage-header">
                      {trans.cover_details}
                    </h3>
                  </div>

                  <IndoorUnit unit={indoorWithCalc} />
                  <OutdoorUnit unit={outdoorWithCalc} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AfterSalesLayout>
  );
};

export default CheckWarrantyStatus;
